import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import * as api from 'utils/api';
import { useQuery } from '@tanstack/react-query';
import useTranslation from 'hooks/useTranslation';
import ReactLoading from 'components/Common/ReactLoading';
import orderedBy from 'lodash/orderBy';
import LandingHeader from 'components/Common/LandingHeader';
import styles from './SharedGenAIImage.module.scss';

export default function SharedGenAIImage() {
  const { shareableId } = useParams();
  const { tGenAIImage, tGenAIRoomTypes, tGenAIStyles } = useTranslation();
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ['GetSharedGenAIImage', shareableId],
    queryFn: () => api.getSharedGenAIImage(shareableId),
  });

  const variants = useMemo(() => {
    if (!data) {
      return [];
    }

    return orderedBy(data.variants, (variant) => {
      const createdAt = new Date(variant.createdAt);
      return createdAt.getTime();
    }, ['desc']);
  }, [data]);

  const selectedVariant = variants[selectedVariantIndex];

  useEffect(() => {
    if (variants.length > 0) {
      setSelectedVariantIndex(selectedVariantIndex => {
        if (selectedVariantIndex === null) {
          return 0;
        }

        return selectedVariantIndex;
      });
    }
  }, [variants]);

  const renderContent = () => (
    <>
      <div className={styles.content}>
        <div className={styles.preview}>
          <div className={styles.imageContainer}>
            <img src={selectedVariant.highResFile || selectedVariant.lowResFile} alt="Preview" />
            <div className={styles.info}>
              <span>{tGenAIRoomTypes(data.roomType)}</span>
              <div className={styles.separator} />
              <span>{tGenAIStyles(data.style)}</span>
            </div>
          </div>
        </div>
        <div className={styles.variants}>
          {variants.map((variant, index) => (
            <div
              className={`${styles.variant} ${selectedVariant.id === variant.id ? styles.selected : ''}`}
              key={variant.id}
              onClick={() => {
                setSelectedVariantIndex(index);
              }}
            >
              <img src={variant.highResFile || variant.lowResFile} alt="Variant" />
              <div className={styles.variantName}>
                {tGenAIImage('variants', { index: index + 1 })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.footer}>
        <button
          className={styles.downloadButton}
          type="button"
          onClick={() => {
            window.open(selectedVariant.highResFile || selectedVariant.lowResFile, '_blank');
          }}
        >
          {tGenAIImage('download')}
        </button>
      </div>
    </>
  );

  return (
    <div className={styles.container}>
      <LandingHeader />
      <div className={styles.contentWrapper}>
        {isLoading && <ReactLoading />}
        {data && selectedVariant && renderContent()}
      </div>
    </div>
  );
}
